import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from 'components/common/tooltip';
import { FilterOptionsState } from '@mui/material/useAutocomplete/useAutocomplete';
import { getAutoCompleteDisable } from 'components/medicalHistory/helpers';

export type AutoCompleteOption = {
    value: string;
    label: string;
    reaction?: string;
    year?: string | null;
    dispensableDrugId?: number | null;
    routedDoseFormDrugId?: number | null;
};

type Props = {
    hide?: boolean;
    disabled?: boolean;
    options: AutoCompleteOption[];
    value: AutoCompleteOption[];
    placeholder?: string;
    noOptionsText?: string;
    onChange?: (
        event: React.SyntheticEvent,
        value: any,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<AutoCompleteOption>,
    ) => void;
    onInputChange?: (
        event: React.SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason,
    ) => void;
    filterOptions?: (
      options: AutoCompleteOption[],
      state: FilterOptionsState<AutoCompleteOption>,
      selected?: AutoCompleteOption[],
      ) => AutoCompleteOption[];
    popupIcon?: React.ReactNode;
    disabledTooltip?: string;
};

function MuiAutoComplete({
  options: option,
  placeholder,
  onChange,
  value: inputValue,
  onInputChange,
  noOptionsText,
  filterOptions,
  hide,
  disabled,
  popupIcon,
  disabledTooltip,
}: Props) {
  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      variant="outlined"
      placeholder={placeholder ?? ''}
      disabled={disabled}
    />
  );

  const filterOptionsSelected = (
    options: AutoCompleteOption[],
    state: FilterOptionsState<AutoCompleteOption>,
  ) => (typeof filterOptions === 'function' ? filterOptions(options, state, inputValue) : options);

  const icon = popupIcon ?? <SearchIcon className="search-icon" />;

  return (!hide
    ? (
      <Tooltip title={disabledTooltip} hide={!disabled}>
        <Autocomplete
          options={option}
          renderTags={() => null}
          clearIcon={null}
          popupIcon={icon}
          autoHighlight
          getOptionLabel={(option) => option.label}
          renderInput={(params) => renderInput(params)}
          getOptionDisabled={(item) => getAutoCompleteDisable(option, inputValue, item)}
          multiple
          value={inputValue}
          onChange={onChange}
          onInputChange={onInputChange}
          noOptionsText={noOptionsText ?? 'No option/duplicate allowed'}
          filterOptions={filterOptionsSelected}
          disabled={disabled}
        />
      </Tooltip>
    ) : null
  );
}

export default MuiAutoComplete;
